/**
 * ProjectCard - Card expandable cho mỗi dự án
 *
 * NEW DESIGN:
 * - Tất cả folders hiển thị dạng TABS (không dùng expandable cards)
 * - Multi-level tabs: Level 1 → Level 2 → Level 3...
 * - Auto-close: Mở folder mới → đóng folder cũ ở cùng level
 * - Files chỉ hiển thị của folder active cuối cùng
 * - Breadcrumb navigation
 * - Selection mode: Cho phép chọn nhiều file để tải về
 */
import { useState, useRef, useMemo, useCallback } from 'react'
import clsx from 'clsx'
import {
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineFolder,
  HiOutlineFolderOpen,
  HiOutlineDocumentText,
  HiOutlineViewGrid,
  HiOutlineViewList,
  HiOutlineSearch,
  HiOutlineX,
  HiOutlineHome,
  HiOutlineSortAscending,
  HiOutlineSortDescending,
  HiOutlineDownload,
  HiOutlineCheckCircle,
} from 'react-icons/hi'
import toast from 'react-hot-toast'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import FileCard from './FileCard'
import driveService from '../../services/driveService'

// Sort options for files
const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Ten A-Z', icon: HiOutlineSortAscending },
  { value: 'name_desc', label: 'Ten Z-A', icon: HiOutlineSortDescending },
  { value: 'date_desc', label: 'Moi nhat', icon: HiOutlineSortDescending },
  { value: 'date_asc', label: 'Cu nhat', icon: HiOutlineSortAscending },
  { value: 'size_desc', label: 'Lon nhat', icon: HiOutlineSortDescending },
  { value: 'size_asc', label: 'Nho nhat', icon: HiOutlineSortAscending },
]

/**
 * Breadcrumb - Hiển thị đường dẫn folder hierarchy
 */
function Breadcrumb({ path, onNavigate }) {
  if (!path || path.length === 0) return null

  return (
    <nav className="flex items-center gap-1 text-sm overflow-x-auto pb-2 scrollbar-hide">
      {path.map((item, index) => (
        <div key={item.drive_id || index} className="flex items-center gap-1 flex-shrink-0">
          {index > 0 && (
            <HiOutlineChevronRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
          )}
          {index === 0 ? (
            <button
              onClick={() => onNavigate(index)}
              className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors text-gray-600 hover:text-gray-900"
            >
              <HiOutlineHome className="w-3.5 h-3.5" />
              <span className="max-w-[100px] truncate">{item.name}</span>
            </button>
          ) : index === path.length - 1 ? (
            <span className="flex items-center gap-1 px-2 py-1 text-primary-600 font-medium">
              <HiOutlineFolderOpen className="w-3.5 h-3.5" />
              <span className="max-w-[120px] truncate">{item.name}</span>
            </span>
          ) : (
            <button
              onClick={() => onNavigate(index)}
              className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors text-gray-600 hover:text-gray-900"
            >
              <HiOutlineFolder className="w-3.5 h-3.5" />
              <span className="max-w-[100px] truncate">{item.name}</span>
            </button>
          )}
        </div>
      ))}
    </nav>
  )
}

/**
 * FolderTabs - Hiển thị một row của folder tabs
 * @param {Array} folders - Danh sách folders
 * @param {string} activeId - drive_id của folder đang active
 * @param {Function} onSelect - Handler khi click folder
 * @param {boolean} loading - Loading state của folder đang active
 * @param {number} level - Level depth (0 = root, 1 = subfolder, etc.)
 */
function FolderTabs({ folders, activeId, onSelect, loading, level = 0 }) {
  if (!folders || folders.length === 0) return null

  // Sort folders by number prefix
  const sortedFolders = useMemo(() => {
    return [...folders].sort((a, b) => {
      const getNumber = (name) => {
        const match = name?.match(/^(\d+)[\.\s]/)
        return match ? parseInt(match[1], 10) : 999
      }
      return getNumber(a.name) - getNumber(b.name)
    })
  }, [folders])

  // Different background colors for different levels
  const levelStyles = {
    0: '', // Level 0 - no background
    1: 'bg-gray-50 -mx-4 md:-mx-5 px-4 md:px-5 py-3 rounded-lg', // Level 1 - light gray
    2: 'bg-gray-100 -mx-4 md:-mx-5 px-4 md:px-5 py-3 rounded-lg', // Level 2 - darker
  }

  return (
    <div className={clsx('mb-3', levelStyles[level] || levelStyles[2])}>
      {level > 0 && (
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
          Thu muc con ({folders.length})
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {sortedFolders.map((folder) => {
          const isActive = activeId === folder.drive_id
          const fileCount = folder.file_count || 0

          return (
            <button
              key={folder.drive_id}
              onClick={() => onSelect(folder)}
              title={folder.name}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all',
                'border min-w-0 relative',
                isActive
                  ? 'bg-primary-50 text-primary-700 border-primary-200 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
              )}
            >
              {isActive ? (
                <HiOutlineFolderOpen className="w-4 h-4 flex-shrink-0" />
              ) : (
                <HiOutlineFolder className="w-4 h-4 flex-shrink-0" />
              )}
              <span className={clsx(
                'min-w-0 text-left',
                fileCount > 0 ? 'pr-6' : '',
                isActive ? 'whitespace-normal break-words' : 'truncate'
              )}>
                {folder.name}
              </span>
              {fileCount > 0 && (
                <span className={clsx(
                  'absolute right-2 top-1/2 -translate-y-1/2 text-xs px-1.5 py-0.5 rounded-full',
                  isActive ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500'
                )}>
                  {fileCount}
                </span>
              )}
              {loading && isActive && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/**
 * ProjectCard - Main component
 * Hiển thị cấu trúc thư mục thực tế từ Drive với multi-level tabs
 */
function ProjectCard({ project, onCopyLink, onPreviewFile, isExpanded, onToggleExpand }) {
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState(null)
  const [viewMode, setViewMode] = useState('grid') // grid | list
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState('name_asc')
  const [showSortMenu, setShowSortMenu] = useState(false)
  const contentRef = useRef(null)

  // Multi-level folder state
  // activePath: Array of { folder, contents } for each level
  // activePath[0] = level 1 folder, activePath[1] = level 2 folder, etc.
  const [activePath, setActivePath] = useState([])
  const [loadingLevel, setLoadingLevel] = useState(-1) // Which level is loading

  // Selection state for download
  const [selectedFiles, setSelectedFiles] = useState({}) // { [drive_id]: file }
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 })

  // Sort folders by number prefix helper
  const sortFoldersByNumber = useCallback((folders) => {
    return [...folders].sort((a, b) => {
      const getNumber = (name) => {
        const match = name?.match(/^(\d+)[\.\s]/)
        return match ? parseInt(match[1], 10) : 999
      }
      return getNumber(a.name) - getNumber(b.name)
    })
  }, [])

  // Get current display data - MUST be defined before selection handlers
  const rootFolders = useMemo(() => {
    return sortFoldersByNumber(detail?.more_folders || [])
  }, [detail?.more_folders, sortFoldersByNumber])

  // Get the deepest active folder's data
  const currentLevel = activePath.length - 1
  const currentContents = currentLevel >= 0 ? activePath[currentLevel].contents : null
  const currentFiles = currentContents?.files || []
  const currentSubfolders = currentContents?.subfolders || []

  // Sort function for files
  const sortFiles = useCallback((fileList, sortKey) => {
    const sorted = [...fileList]
    switch (sortKey) {
      case 'name_asc':
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      case 'name_desc':
        return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''))
      case 'date_desc':
        return sorted.sort((a, b) => new Date(b.modified_at || 0) - new Date(a.modified_at || 0))
      case 'date_asc':
        return sorted.sort((a, b) => new Date(a.modified_at || 0) - new Date(b.modified_at || 0))
      case 'size_desc':
        return sorted.sort((a, b) => (b.file_size || 0) - (a.file_size || 0))
      case 'size_asc':
        return sorted.sort((a, b) => (a.file_size || 0) - (b.file_size || 0))
      default:
        return sorted
    }
  }, [])

  // Filter and sort files - MUST be defined before handleSelectAll
  const filteredFiles = useMemo(() => {
    let result = currentFiles
    if (searchQuery?.trim()) {
      const query = searchQuery.toLowerCase()
      result = currentFiles.filter(f => f.name?.toLowerCase().includes(query))
    }
    return sortFiles(result, sortBy)
  }, [currentFiles, searchQuery, sortBy, sortFiles])

  const totalFilteredCount = useMemo(() => {
    if (!searchQuery?.trim()) return null
    return filteredFiles.length
  }, [filteredFiles, searchQuery])

  // Get breadcrumb path from activePath
  const breadcrumbPath = useMemo(() => {
    if (activePath.length === 0) return []
    return [
      { name: project.name, drive_id: project.drive_id },
      ...activePath.map(item => item.folder)
    ]
  }, [activePath, project])

  // Handle expand/collapse project
  const handleExpand = async () => {
    if (isExpanded) {
      onToggleExpand(null)
      setSearchQuery('')
      setActivePath([])
      return
    }

    // Load detail if not loaded
    if (!detail) {
      setLoading(true)
      const response = await driveService.getProjectDetail(project.drive_id)
      if (response.status === 'success' && response.data) {
        setDetail(response.data)
        // Auto-select first folder
        const folders = response.data.more_folders || []
        if (folders.length > 0) {
          const sorted = sortFoldersByNumber(folders)
          await handleSelectFolder(sorted[0], 0)
        }
      }
      setLoading(false)
    } else if (activePath.length === 0) {
      // Re-select first folder when re-expanding
      const folders = detail.more_folders || []
      if (folders.length > 0) {
        const sorted = sortFoldersByNumber(folders)
        await handleSelectFolder(sorted[0], 0)
      }
    }

    onToggleExpand(project.id)
  }

  // Handle selecting a folder at a specific level
  const handleSelectFolder = async (folder, level) => {
    // Check if this folder is already active at this level
    if (activePath[level]?.folder.drive_id === folder.drive_id) {
      return
    }

    // Clear selection when changing folder
    setSelectedFiles({})
    setLoadingLevel(level)

    try {
      const response = await driveService.getFolderContents(folder.drive_id)
      if (response.status === 'success' && response.data) {
        // Update activePath: keep levels before this, replace this level, remove levels after
        setActivePath(prev => {
          const newPath = prev.slice(0, level)
          newPath[level] = { folder, contents: response.data }
          return newPath
        })
      }
    } finally {
      setLoadingLevel(-1)
    }
  }

  // Handle breadcrumb navigation
  const handleBreadcrumbNavigate = useCallback((index) => {
    if (index === 0) {
      // Navigate to project root - clear all
      setActivePath([])
    } else {
      // Navigate to specific level - keep up to that level
      setActivePath(prev => prev.slice(0, index))
    }
  }, [])

  // Selection handlers
  const selectedCount = Object.keys(selectedFiles).length
  const selectionMode = selectedCount > 0

  const handleFileSelect = useCallback((file, isSelected) => {
    setSelectedFiles(prev => {
      if (isSelected) {
        return { ...prev, [file.drive_id]: file }
      } else {
        const { [file.drive_id]: removed, ...rest } = prev
        return rest
      }
    })
  }, [])

  const handleSelectAll = useCallback(() => {
    const allSelected = filteredFiles.reduce((acc, file) => {
      acc[file.drive_id] = file
      return acc
    }, {})
    setSelectedFiles(allSelected)
  }, [filteredFiles])

  const handleClearSelection = useCallback(() => {
    setSelectedFiles({})
  }, [])

  // Download functions
  const handleDownloadSelected = useCallback(async () => {
    const files = Object.values(selectedFiles)
    if (files.length === 0) return

    setIsDownloading(true)
    setDownloadProgress({ current: 0, total: files.length })

    try {
      if (files.length === 1) {
        // Single file - direct download
        const file = files[0]
        toast.loading('Đang tải file...', { id: 'download' })

        const response = await fetch(`/api/drive/public/download/${file.drive_id}`)
        if (!response.ok) throw new Error('Download failed')

        const blob = await response.blob()
        saveAs(blob, file.name)

        toast.success('Tải file thành công!', { id: 'download' })
      } else {
        // Multiple files - create ZIP
        toast.loading(`Đang tải ${files.length} files...`, { id: 'download' })

        const zip = new JSZip()
        let completed = 0

        for (const file of files) {
          try {
            const response = await fetch(`/api/drive/public/download/${file.drive_id}`)
            if (response.ok) {
              const blob = await response.blob()
              zip.file(file.name, blob)
            }
            completed++
            setDownloadProgress({ current: completed, total: files.length })
            toast.loading(`Đang tải ${completed}/${files.length} files...`, { id: 'download' })
          } catch (err) {
            console.error(`Failed to download ${file.name}:`, err)
          }
        }

        // Generate ZIP
        toast.loading('Đang nén files...', { id: 'download' })
        const zipBlob = await zip.generateAsync({
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: { level: 6 }
        })

        // Get folder name for ZIP filename
        const folderName = activePath.length > 0
          ? activePath[activePath.length - 1].folder.name
          : project.name
        const zipName = `${folderName.replace(/[^a-zA-Z0-9\u00C0-\u024F\u1E00-\u1EFF]/g, '_')}.zip`

        saveAs(zipBlob, zipName)
        toast.success(`Đã tải ${completed} files thành công!`, { id: 'download' })
      }

      // Clear selection after download
      setSelectedFiles({})
    } catch (error) {
      console.error('Download error:', error)
      toast.error('Có lỗi khi tải files', { id: 'download' })
    } finally {
      setIsDownloading(false)
      setDownloadProgress({ current: 0, total: 0 })
    }
  }, [selectedFiles, activePath, project.name])

  return (
    <div
      className={clsx(
        'bg-white rounded-2xl shadow-sm border overflow-hidden transition-all duration-500 ease-in-out',
        isExpanded
          ? 'border-primary-200 shadow-lg col-span-full'
          : 'border-gray-100 hover:border-gray-200 hover:shadow-md'
      )}
    >
      {/* Header - Click to expand */}
      <button
        onClick={handleExpand}
        className="w-full p-4 md:p-5 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={clsx(
            'w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300',
            isExpanded ? 'bg-primary-100 scale-110' : 'bg-gray-100'
          )}>
            {isExpanded ? (
              <HiOutlineFolderOpen className="w-7 h-7 text-primary-600" />
            ) : (
              <HiOutlineFolder className="w-7 h-7 text-gray-500" />
            )}
          </div>
          <div className="text-left flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-lg">{project.name}</h3>
            {project.description && (
              <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                {project.description}
              </p>
            )}
            <p className="text-sm text-gray-500 mt-0.5">
              {project.file_count || 0} tai lieu
            </p>
          </div>
        </div>

        <HiOutlineChevronDown
          className={clsx(
            'w-6 h-6 text-gray-400 transition-transform duration-300',
            isExpanded && 'rotate-180'
          )}
        />
      </button>

      {/* Expanded content */}
      <div
        ref={contentRef}
        className={clsx(
          'overflow-hidden transition-all duration-500 ease-in-out',
          isExpanded ? 'opacity-100' : 'max-h-0 opacity-0'
        )}
        style={{
          maxHeight: isExpanded ? '5000px' : '0px'
        }}
      >
        {loading ? (
          <div className="p-6 border-t border-gray-100">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
            </div>
          </div>
        ) : (
          <div className="border-t border-gray-100">
            {/* Search bar */}
            <div className="px-4 md:px-5 pt-4">
              <div className="relative">
                <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={`Tim kiem trong "${project.name}"...`}
                  className="w-full pl-9 pr-9 py-2.5 bg-gray-100 border-0 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                  onClick={(e) => e.stopPropagation()}
                />
                {searchQuery && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setSearchQuery('')
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 rounded-full"
                  >
                    <HiOutlineX className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
              {totalFilteredCount !== null && (
                <p className="text-xs text-gray-500 mt-2">
                  Tim thay {totalFilteredCount} file phu hop
                </p>
              )}
            </div>

            {/* Folder content area */}
            {rootFolders.length > 0 && (
              <div className="px-4 md:px-5 pt-4">
                {/* Breadcrumb navigation */}
                {breadcrumbPath.length > 0 && (
                  <div className="mb-3">
                    <Breadcrumb path={breadcrumbPath} onNavigate={handleBreadcrumbNavigate} />
                  </div>
                )}

                {/* Toolbar: Sort + View mode */}
                {/* <div className="flex items-center justify-between mb-3">
                  <div className="relative">
                    <button
                      onClick={() => setShowSortMenu(!showSortMenu)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {SORT_OPTIONS.find(o => o.value === sortBy)?.icon && (
                        <span className="w-4 h-4">
                          {(() => {
                            const Icon = SORT_OPTIONS.find(o => o.value === sortBy)?.icon
                            return Icon ? <Icon className="w-4 h-4" /> : null
                          })()}
                        </span>
                      )}
                      <span>{SORT_OPTIONS.find(o => o.value === sortBy)?.label || 'Sap xep'}</span>
                      <HiOutlineChevronDown className={clsx(
                        'w-3 h-3 transition-transform',
                        showSortMenu && 'rotate-180'
                      )} />
                    </button>

                    {showSortMenu && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowSortMenu(false)}
                        />
                        <div className="absolute left-0 top-full mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                          {SORT_OPTIONS.map((option) => {
                            const Icon = option.icon
                            return (
                              <button
                                key={option.value}
                                onClick={() => {
                                  setSortBy(option.value)
                                  setShowSortMenu(false)
                                }}
                                className={clsx(
                                  'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left',
                                  sortBy === option.value
                                    ? 'bg-primary-50 text-primary-700'
                                    : 'text-gray-700 hover:bg-gray-50'
                                )}
                              >
                                <Icon className="w-4 h-4" />
                                {option.label}
                              </button>
                            )
                          })}
                        </div>
                      </>
                    )}
                  </div>

                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={clsx(
                        'p-1.5 rounded-md transition-colors',
                        viewMode === 'grid'
                          ? 'bg-white shadow-sm text-primary-600'
                          : 'text-gray-400 hover:text-gray-600'
                      )}
                      title="Dang luoi"
                    >
                      <HiOutlineViewGrid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={clsx(
                        'p-1.5 rounded-md transition-colors',
                        viewMode === 'list'
                          ? 'bg-white shadow-sm text-primary-600'
                          : 'text-gray-400 hover:text-gray-600'
                      )}
                      title="Dang danh sach"
                    >
                      <HiOutlineViewList className="w-4 h-4" />
                    </button>
                  </div>
                </div> */}

                {/* Level 0: Root folders tabs */}
                <FolderTabs
                  folders={rootFolders}
                  activeId={activePath[0]?.folder.drive_id}
                  onSelect={(folder) => handleSelectFolder(folder, 0)}
                  loading={loadingLevel === 0}
                  level={0}
                />

                {/* Level 1+: Subfolders tabs (nếu có) */}
                {activePath.map((pathItem, levelIndex) => {
                  const subfolders = pathItem.contents?.subfolders || []
                  if (subfolders.length === 0) return null

                  const nextLevelActiveId = activePath[levelIndex + 1]?.folder.drive_id

                  return (
                    <FolderTabs
                      key={`level-${levelIndex + 1}-${pathItem.folder.drive_id}`}
                      folders={subfolders}
                      activeId={nextLevelActiveId}
                      onSelect={(folder) => handleSelectFolder(folder, levelIndex + 1)}
                      loading={loadingLevel === levelIndex + 1}
                      level={levelIndex + 1}
                    />
                  )
                })}

                {/* Loading state */}
                {loadingLevel >= 0 && (
                  <div className="py-8 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
                    <span className="ml-2 text-sm text-gray-500">Đang tải...</span>
                  </div>
                )}

                {/* Files grid - chỉ hiển thị files của folder active cuối cùng */}
                {loadingLevel < 0 && filteredFiles.length > 0 && (
                  <div className="mt-4">
                    {/* Files header with selection actions */}
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Files ({filteredFiles.length})
                      </p>

                      {/* Selection toolbar */}
                      <div className="flex items-center gap-2">
                        {selectedCount > 0 ? (
                          <>
                            <span className="text-sm text-primary-600 font-medium">
                              Đã chọn {selectedCount}
                            </span>
                            <button
                              onClick={handleClearSelection}
                              className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                            >
                              Bỏ chọn
                            </button>
                            <button
                              onClick={handleDownloadSelected}
                              disabled={isDownloading}
                              className={clsx(
                                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                                isDownloading
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                              )}
                            >
                              {isDownloading ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
                                  <span>{downloadProgress.current}/{downloadProgress.total}</span>
                                </>
                              ) : (
                                <>
                                  <HiOutlineDownload className="w-4 h-4" />
                                  <span>Tải về {selectedCount > 1 ? '(ZIP)' : ''}</span>
                                </>
                              )}
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={handleSelectAll}
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary-600 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                          >
                            <HiOutlineCheckCircle className="w-4 h-4" />
                            <span>Chọn tất cả</span>
                          </button>
                        )}
                      </div>
                    </div>

                    <div className={clsx(
                      viewMode === 'grid'
                        ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3'
                        : 'space-y-2'
                    )}>
                      {filteredFiles.map((file, index) => (
                        <FileCard
                          key={file.id}
                          file={file}
                          variant={viewMode}
                          onPreview={onPreviewFile}
                          onCopyLink={onCopyLink}
                          priority={index < 6}
                          selectionMode={selectionMode}
                          isSelected={!!selectedFiles[file.drive_id]}
                          onSelect={handleFileSelect}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state - no files in current folder */}
                {loadingLevel < 0 && activePath.length > 0 && filteredFiles.length === 0 && currentSubfolders.length === 0 && (
                  <div className="py-8 text-center">
                    <HiOutlineDocumentText className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">
                      {searchQuery ? 'Khong tim thay file phu hop' : 'Chua co file trong thu muc nay'}
                    </p>
                  </div>
                )}

                {/* Hint: click subfolder to see its contents */}
                {loadingLevel < 0 && currentSubfolders.length > 0 && filteredFiles.length === 0 && (
                  <div className="py-4 text-center">
                    <p className="text-sm text-gray-400">
                      Click vào thư mục con để xem nội dung
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Empty state - no folders */}
            {rootFolders.length === 0 && !loading && (
              <div className="py-12 text-center">
                <HiOutlineDocumentText className="w-16 h-16 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">Chưa có thư mục trong dự án này</p>
              </div>
            )}

            {/* Bottom padding */}
            <div className="h-4" />
          </div>
        )}
      </div>
    </div>
  )
}

export default ProjectCard
